/**
 * VIN(차대번호) 체크섬 검증 유틸리티
 * VIN의 9번째 자리(인덱스 8)는 체크 디지트로, 나머지 16자리로부터 계산됨
 */

/**
 * VIN 문자를 숫자로 변환 (체크섬 계산용)
 * @param {string} char - VIN 문자 (1자)
 * @returns {number} - 숫자 값
 */
function vinCharToNumber(char) {
  const charMap = {
    'A': 1, 'B': 2, 'C': 3, 'D': 4, 'E': 5, 'F': 6, 'G': 7, 'H': 8,
    'J': 1, 'K': 2, 'L': 3, 'M': 4, 'N': 5, 'P': 7, 'R': 9,
    'S': 2, 'T': 3, 'U': 4, 'V': 5, 'W': 6, 'X': 7, 'Y': 8, 'Z': 9
  };
  
  // I, O, Q는 VIN에 사용되지 않음
  if (charMap[char]) {
    return charMap[char];
  }
  
  // 숫자는 그대로
  if (char >= '0' && char <= '9') {
    return parseInt(char, 10);
  }
  
  return 0;
}

/**
 * VIN 체크섬 검증
 * VIN의 9번째 자리(인덱스 8)는 체크 디지트로, 나머지 16자리로부터 계산됨
 * @param {string} vin - 차대번호 (17자리)
 * @returns {Object} - { isValid: boolean, expectedCheckDigit: string, actualCheckDigit: string, message: string }
 */
export function validateVinChecksum(vin) {
  if (!vin || typeof vin !== 'string') {
    return { isValid: false, expectedCheckDigit: null, actualCheckDigit: null, message: 'VIN이 없습니다' };
  }
  
  const normalizedVIN = vin.trim().toUpperCase();
  
  // VIN은 17자리여야 함
  if (normalizedVIN.length !== 17) {
    return { isValid: false, expectedCheckDigit: null, actualCheckDigit: null, message: `VIN 길이 불일치: ${normalizedVIN.length}자 (17자리 필요)` };
  }
  
  // I, O, Q는 VIN에 사용되지 않음
  if (/[IOQ]/.test(normalizedVIN)) {
    return { isValid: false, expectedCheckDigit: null, actualCheckDigit: normalizedVIN.charAt(8), message: 'VIN에 I, O, Q 문자가 포함되어 있습니다 (사용 불가)' };
  }
  
  // 가중치 (위치별)
  const weights = [8, 7, 6, 5, 4, 3, 2, 10, 0, 9, 8, 7, 6, 5, 4, 3, 2];
  
  // 체크 디지트 계산
  let sum = 0;
  for (let i = 0; i < 17; i++) {
    if (i === 8) continue; // 9번째 자리(체크 디지트)는 제외
    const charValue = vinCharToNumber(normalizedVIN.charAt(i));
    sum += charValue * weights[i];
  }
  
  const remainder = sum % 11;
  const expectedCheckDigit = remainder === 10 ? 'X' : String(remainder);
  const actualCheckDigit = normalizedVIN.charAt(8);
  
  const isValid = expectedCheckDigit === actualCheckDigit;
  
  return {
    isValid,
    expectedCheckDigit,
    actualCheckDigit,
    message: isValid 
      ? '✅ VIN 체크섬 검증 통과' 
      : `❌ VIN 체크섬 오류: 예상값 "${expectedCheckDigit}", 실제값 "${actualCheckDigit}"`
  };
}

/**
 * VIN 기본 검증 (길이, 문자 유효성)
 * @param {string} vin - 차대번호
 * @returns {Object} - { isValid: boolean, message: string }
 */
export function validateVinFormat(vin) {
  if (!vin || typeof vin !== 'string') {
    return { isValid: false, message: 'VIN이 없습니다' };
  }
  
  const normalizedVIN = vin.trim().toUpperCase();
  
  if (normalizedVIN.length !== 17) {
    return { isValid: false, message: `VIN 길이 불일치: ${normalizedVIN.length}자 (17자리 필요)` };
  }
  
  // I, O, Q는 VIN에 사용되지 않음
  if (/[IOQ]/.test(normalizedVIN)) {
    return { isValid: false, message: 'VIN에 I, O, Q 문자가 포함되어 있습니다 (사용 불가)' };
  }
  
  // 영문자와 숫자만 허용
  if (!/^[A-HJ-NPR-Z0-9]{17}$/.test(normalizedVIN)) {
    return { isValid: false, message: 'VIN은 영문자와 숫자만 허용됩니다' };
  }
  
  return { isValid: true, message: '✅ VIN 형식 검증 통과' };
}

